import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const dealsApi = createApi({
  reducerPath: "dealsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Deals", "Pipeline", "PipelineSummary"],
  endpoints: (build) => ({
    // Get all deals with filters
    getDeals: build.query({
      query: (params = {}) => ({
        url: "/deals",
        method: "GET",
        params,
      }),
      providesTags: ["Deals"],
      transformResponse: (response) => response.data || response,
    }),

    // Get deals grouped by pipeline stage
    getDealsPipeline: build.query({
      query: (params = {}) => ({
        url: "/deals/pipeline",
        method: "GET",
        params,
      }),
      providesTags: ["Pipeline", "Deals"],
      transformResponse: (response) => ({
        pipeline: response.data,
        totalDeals: response.totalDeals,
      }),
    }),

    // Get pipeline summary statistics
    getPipelineSummary: build.query({
      query: () => ({
        url: "/deals/pipeline/summary",
        method: "GET",
      }),
      providesTags: ["PipelineSummary"],
      transformResponse: (response) => response.data || response,
    }),

    // Get deals needing attention
    getDealsNeedingAttention: build.query({
      query: (params = {}) => ({
        url: "/deals/needs-attention",
        method: "GET",
        params,
      }),
      providesTags: ["Deals"],
      transformResponse: (response) => response.data || response,
    }),

    // Get single deal by ID
    getDealById: build.query({
      query: (id) => ({
        url: `/deals/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Deals", id }],
      transformResponse: (response) => response.data || response,
    }),

    // Create new deal
    createDeal: build.mutation({
      query: (body) => ({
        url: "/deals",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Deals", "Pipeline", "PipelineSummary"],
    }),

    // Create deal from client
    createDealFromClient: build.mutation({
      query: (clientId) => ({
        url: `/deals/from-client/${clientId}`,
        method: "POST",
      }),
      invalidatesTags: ["Deals", "Pipeline", "PipelineSummary"],
    }),

    // Update deal
    updateDeal: build.mutation({
      query: ({ id, ...body }) => ({
        url: `/deals/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Deals", id },
        "Deals",
        "Pipeline",
        "PipelineSummary",
      ],
    }),

    // Update deal stage (for drag-and-drop)
    updateDealStage: build.mutation({
      query: ({ id, stage, manualOverride = true }) => ({
        url: `/deals/${id}/stage`,
        method: "PATCH",
        body: { stage, manualOverride },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Deals", id },
        "Deals",
        "Pipeline",
        "PipelineSummary",
      ],
      // Optimistic update for smooth drag-and-drop
      async onQueryStarted({ id, stage }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          dealsApi.util.updateQueryData("getDealsPipeline", {}, (draft) => {
            // Find and move the deal in the pipeline
            if (draft?.pipeline) {
              for (const stageKey of Object.keys(draft.pipeline)) {
                const dealIndex = draft.pipeline[stageKey].deals?.findIndex(
                  (d) => d._id === id
                );
                if (dealIndex !== -1 && dealIndex !== undefined) {
                  const [deal] = draft.pipeline[stageKey].deals.splice(dealIndex, 1);
                  draft.pipeline[stageKey].count--;
                  if (draft.pipeline[stage]) {
                    deal.stage = stage;
                    draft.pipeline[stage].deals.push(deal);
                    draft.pipeline[stage].count++;
                  }
                  break;
                }
              }
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    // Delete deal
    deleteDeal: build.mutation({
      query: (id) => ({
        url: `/deals/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Deals", "Pipeline", "PipelineSummary"],
    }),

    // Archive deal
    archiveDeal: build.mutation({
      query: ({ id, reason }) => ({
        url: `/deals/${id}/archive`,
        method: "POST",
        body: { reason },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Deals", id },
        "Deals",
        "Pipeline",
        "PipelineSummary",
      ],
    }),

    // Restore archived deal
    restoreDeal: build.mutation({
      query: (id) => ({
        url: `/deals/${id}/restore`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Deals", id },
        "Deals",
        "Pipeline",
        "PipelineSummary",
      ],
    }),

    // Add timeline event
    addTimelineEvent: build.mutation({
      query: ({ id, ...body }) => ({
        url: `/deals/${id}/timeline`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "Deals", id }],
    }),

    // Sync deal from client
    syncDealFromClient: build.mutation({
      query: (id) => ({
        url: `/deals/${id}/sync/client`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Deals", id },
        "Deals",
        "Pipeline",
      ],
    }),

    // Sync deal from transaction
    syncDealFromTransaction: build.mutation({
      query: (id) => ({
        url: `/deals/${id}/sync/transaction`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Deals", id },
        "Deals",
        "Pipeline",
      ],
    }),

    // Sync e-signatures for deal
    syncDealESignatures: build.mutation({
      query: (id) => ({
        url: `/deals/${id}/sync/esignatures`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [{ type: "Deals", id }],
    }),

    // Bulk sync all deals
    bulkSyncDeals: build.mutation({
      query: () => ({
        url: "/deals/sync/all",
        method: "POST",
      }),
      invalidatesTags: ["Deals", "Pipeline", "PipelineSummary"],
    }),
  }),
});

export const {
  useGetDealsQuery,
  useGetDealsPipelineQuery,
  useGetPipelineSummaryQuery,
  useGetDealsNeedingAttentionQuery,
  useGetDealByIdQuery,
  useCreateDealMutation,
  useCreateDealFromClientMutation,
  useUpdateDealMutation,
  useUpdateDealStageMutation,
  useDeleteDealMutation,
  useArchiveDealMutation,
  useRestoreDealMutation,
  useAddTimelineEventMutation,
  useSyncDealFromClientMutation,
  useSyncDealFromTransactionMutation,
  useSyncDealESignaturesMutation,
  useBulkSyncDealsMutation,
} = dealsApi;
